import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Ensure we always have /api in the base URL
const getApiBaseUrl = () => {
  const baseUrl = process.env.REACT_APP_API_URL || 'https://ventro.energy/api';
  // If base URL doesn't end with /api, add it
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const ApiHealthCheck = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Debug log
  console.log('🎨 ApiHealthCheck component rendered', {
    loading,
    hasData: !!healthData,
    hasError: !!error
  });

  const fetchHealthCheck = async () => {
    // In development, use full backend URL. In production, use relative URL.
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isDevelopment ? 'http://192.168.58.131:8000' : '';
    const healthCheckUrl = `${backendUrl}/api/health-check/`;

    console.log('🔍 Fetching health check from:', healthCheckUrl);
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(healthCheckUrl);
      console.log('✅ Health check response:', response.data);
      setHealthData(response.data);
      setLastCheck(new Date());
    } catch (err) {
      console.error('❌ Health check error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setError(err.response?.data?.message || err.message || 'Failed to fetch health check data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthCheck();
  }, []);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchHealthCheck();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok':
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
      case 'degraded':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error':
      case 'unhealthy':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok':
      case 'healthy':
        return '✅';
      case 'warning':
      case 'degraded':
        return '⚠️';
      case 'error':
      case 'unhealthy':
        return '❌';
      default:
        return '❓';
    }
  };

  const getOverallStatusBadge = () => {
    if (!healthData) return null;

    const { status } = healthData;
    const colorClass = getStatusColor(status);

    return (
      <div className={`inline-flex items-center px-4 py-2 rounded-lg border-2 ${colorClass} font-semibold`}>
        <span className="text-2xl mr-2">{getStatusIcon(status)}</span>
        <span className="uppercase">{status}</span>
      </div>
    );
  };

  // Debug view - remove after testing
  if (process.env.NODE_ENV === 'development') {
    console.log('Component state:', { loading, healthData, error, lastCheck });
  }

  if (loading && !healthData) {
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isDevelopment ? 'http://192.168.58.131:8000' : '';
    const displayUrl = `${backendUrl}/api/health-check/`;

    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Checking API status...</p>
          <p className="text-sm text-gray-400 mt-2">URL: {displayUrl}</p>
          <p className="text-xs text-gray-400 mt-1">If this takes too long, check console (F12)</p>
        </div>
      </div>
    );
  }

  if (error && !healthData) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
        <div className="text-5xl mb-4">❌</div>
        <h3 className="text-xl font-semibold text-red-800 mb-2">Health Check Failed</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchHealthCheck}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">API Health Check</h1>
            <p className="text-gray-600">System status and diagnostics</p>
          </div>
          <div className="text-right">
            {getOverallStatusBadge()}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-4">
            <button
              onClick={fetchHealthCheck}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              Refresh Now
            </button>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Auto-refresh (30s)</span>
            </label>
          </div>

          <div className="text-sm text-gray-500">
            {lastCheck && (
              <>
                Last checked: {lastCheck.toLocaleTimeString()}
                <span className="ml-2">
                  ({healthData?.response_time_ms}ms)
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      {healthData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{healthData.summary.total_checks}</div>
            <div className="text-sm text-gray-600">Total Checks</div>
          </div>
          <div className="bg-green-50 rounded-lg shadow p-4 text-center border-2 border-green-200">
            <div className="text-2xl font-bold text-green-600">{healthData.summary.passed}</div>
            <div className="text-sm text-green-700">Passed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg shadow p-4 text-center border-2 border-yellow-200">
            <div className="text-2xl font-bold text-yellow-600">{healthData.summary.warnings}</div>
            <div className="text-sm text-yellow-700">Warnings</div>
          </div>
          <div className="bg-red-50 rounded-lg shadow p-4 text-center border-2 border-red-200">
            <div className="text-2xl font-bold text-red-600">{healthData.summary.errors}</div>
            <div className="text-sm text-red-700">Errors</div>
          </div>
          <div className="bg-blue-50 rounded-lg shadow p-4 text-center border-2 border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{healthData.summary.health_percentage}%</div>
            <div className="text-sm text-blue-700">Health Score</div>
          </div>
        </div>
      )}

      {/* Detailed Checks */}
      <div className="space-y-4">
        {healthData?.checks && Object.entries(healthData.checks).map(([key, check]) => (
          <div
            key={key}
            className={`bg-white rounded-lg shadow-lg p-6 border-l-4 ${
              check.status === 'ok' ? 'border-green-500' :
              check.status === 'warning' ? 'border-yellow-500' :
              'border-red-500'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getStatusIcon(check.status)}</span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 capitalize">
                    {key.replace(/_/g, ' ')}
                  </h3>
                  <p className={`text-sm ${
                    check.status === 'ok' ? 'text-green-600' :
                    check.status === 'warning' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {check.message}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(check.status)}`}>
                {check.status}
              </span>
            </div>

            {/* Details */}
            {check.details && (
              <div className="mt-4 bg-gray-50 rounded p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Details:</h4>
                <pre className="text-xs text-gray-600 overflow-x-auto">
                  {JSON.stringify(check.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Timestamp */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Health check performed at: {healthData?.timestamp}
      </div>
    </div>
  );
};

export default ApiHealthCheck;
